import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import { nothing, produce, WritableDraft } from 'immer';
import {
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  take,
  tap,
  UnaryFunction,
  withLatestFrom,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import {
  ComparisonQueryNode,
  CreateWorkflowRequest,
  InputSchemaInfo,
  LogicalType,
  QueryNodeType,
  UpdateWorkflowRequest,
} from '@blaze-shared/api-contracts-domain';
import { ConnectorsCollectionService } from '@blaze-shared/connectors-data-access';
import {
  mapSimpleQueryConditionsToComplexFilters,
  removeNodes,
} from '@blaze-shared/datasets-domain';
import { isBindingActive } from '@blaze-shared/elements-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { compareAsc } from '@blaze-shared/platform-util';
import {
  BaseSchema,
  convertSchemaToInputSchemaInfo,
  getElementSchema,
} from '@blaze-shared/schema-domain';
import { deepClone, isDeepEqual } from '@blaze-shared/shared-util';
import { WorkflowsApiService } from '@blaze-shared/workflow-data-access';
import {
  Connection,
  DataAssignment,
  DEFAULT_WORKFLOW_VERSION,
  findFirstNodeInWorkflowOfType,
  GetWorkflowNode,
  isGetNode,
  isInsertNode,
  isServerOnlyWorkflow,
  isTriggeredWorkflow,
  isUpdateNode,
  Workflow,
  WorkflowInputs,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { fromEngine, RuntimeFacade, State } from '../+state';
import { ElementsFacade } from '../+state/elements';
import { hasDataProps } from '../canvas-elements/element-model/element-props-guards';
import {
  createConnection,
  getNamedInputConnections,
  getUiRuntimeElement,
  getWorkflowInputName,
  getWorkflowRuntimeElement,
} from '../utils/runtime';

import { RuntimeService } from './runtime.service';

@Injectable({ providedIn: 'root' })
export class WorkflowCrudService {
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly notificationService = inject(NotificationService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly runtimeService = inject(RuntimeService);
  private readonly store = inject<Store<State>>(Store);
  private readonly workflowsApiService = inject(WorkflowsApiService);

  /** If element is bound to a form-like element an input connection is added, so form data is available for filtering */
  ensureFormConnection(workflowId: string, elementId: CanvasElementId): Observable<Workflow> {
    return combineLatest({
      workflow: this.selectWorkflowOnce(workflowId),
      elements: this.elementsFacade.entities$,
    }).pipe(
      first(),
      concatMap(({ workflow, elements }) => {
        const element = elements[elementId];
        if (!workflow || !element) {
          throw new Error('Data missing');
        }

        const formContainerBinding = hasDataProps(element)
          ? element.props.data?.find(
              (binding) =>
                isBindingActive(binding) && binding?.dataSource?.sourceType === 'formContainer',
            )
          : undefined;

        if (!formContainerBinding) {
          return of(workflow);
        } // Element not bound to a form container - no action

        const sourceFormElementId = formContainerBinding.dataSource?.id;
        if (!sourceFormElementId) {
          return of(workflow);
        }

        const sourceFormElement = elements[sourceFormElementId];
        if (
          // parent element not form or details view - no action
          sourceFormElement?.type !== 'formContainer' &&
          sourceFormElement?.type !== 'formDetails'
        ) {
          return of(workflow);
        }

        const hasConnectionToParentForm = this.runtimeService
          .getConnectionsBySourceAndEventType(getUiRuntimeElement(sourceFormElementId), 'change')
          .some(({ target }) => target.layerType === 'Workflow' && target.id === workflow.id);

        // If connection to parent form already present - no action
        if (hasConnectionToParentForm) {
          return of(workflow);
        }

        const connectionToFormInputWorkflow = createConnection(
          sourceFormElement.ns,
          getUiRuntimeElement(sourceFormElementId),
          getWorkflowRuntimeElement(workflow.id),
          'change',
        );

        const inputName = sourceFormElement.type === 'formDetails' ? 'details' : 'form';
        const updatedWorkflow = produce(workflow, (draft) => {
          draft.inputs[inputName] = connectionToFormInputWorkflow.id;
        });

        this.store.dispatch(
          runtimeActions.connectionAdd({ connection: connectionToFormInputWorkflow }),
        );

        return of(updatedWorkflow).pipe(this.updateWorkflowAndTriggerUpdate(workflowId));
      }),
    );
  }

  updateAction(workflowId: string, nodes: WorkflowNode[]): Observable<Workflow> {
    return this.selectWorkflowOnce(workflowId).pipe(
      map((workflow) =>
        produce<Workflow | undefined>(workflow, (draft) => {
          if (!draft) {
            return nothing;
          } // return undefined
          draft.startNodeId = nodes[0].id;
          draft.nodes = nodes;
          return undefined;
        }),
      ),
      this.updateWorkflowAndTriggerUpdate(workflowId),
    );
  }

  // #region filter conditions
  addFilterCondition(workflowId: string, condition: ComparisonQueryNode): Observable<Workflow> {
    return this.selectWorkflowOnce(workflowId).pipe(
      map((workflow) =>
        produce<Workflow | undefined>(workflow, (draft) => {
          if (!draft) {
            return nothing;
          } // return undefined
          const getNode = this.getWorkflowGetNodeOrNothing(draft);
          if (!getNode) {
            return nothing;
          }

          getNode.query ??= {
            nodeType: QueryNodeType.Logical,
            logicalType: LogicalType.And,
            children: [],
          };

          getNode.query.children.push(condition);
          return undefined;
        }),
      ),
      this.updateWorkflowAndTriggerUpdate(workflowId),
    );
  }

  updateFilterCondition(
    workflowId: string,
    originalCondition: ComparisonQueryNode,
    condition: ComparisonQueryNode,
  ): Observable<Workflow> {
    return this.selectWorkflowOnce(workflowId).pipe(
      map((workflow) =>
        produce<Workflow | undefined>(workflow, (draft) => {
          if (!draft) {
            return nothing;
          } // return undefined
          const getNode = this.getWorkflowGetNodeOrNothing(draft);
          if (!getNode || (!getNode.query && !getNode.queryConditions.length)) {
            return nothing;
          }

          if (!getNode.query) {
            return nothing;
          }

          const indexToUpdate = (getNode.query.children || []).findIndex((queryCondition) =>
            isEqual(queryCondition, originalCondition),
          );

          if (indexToUpdate === -1) {
            return nothing;
          }

          getNode.query.children[indexToUpdate] = condition;
          return undefined;
        }),
      ),
      this.updateWorkflowAndTriggerUpdate(workflowId),
    );
  }

  removeFilterCondition(workflowId: string, condition: ComparisonQueryNode): Observable<Workflow> {
    return this.selectWorkflowOnce(workflowId).pipe(
      map((workflow) =>
        produce<Workflow | undefined>(workflow, (draft) => {
          if (!draft) {
            return nothing;
          } // return undefined
          const getNode = this.getWorkflowGetNodeOrNothing(draft);
          if (!getNode) {
            return nothing;
          }

          if (!getNode.query) {
            return nothing;
          }

          const removedCount = removeNodes(getNode.query, (node) => isEqual(node, condition));

          if (removedCount === 0) {
            return nothing;
          }
          return undefined;
        }),
      ),
      this.updateWorkflowAndTriggerUpdate(workflowId),
    );
  }

  updateFilterQueryLogicalType(workflowId: string, logicalType: LogicalType): Observable<Workflow> {
    return this.selectWorkflowOnce(workflowId).pipe(
      map((workflow) =>
        produce<Workflow | undefined>(workflow, (draft) => {
          if (!draft) {
            return nothing;
          }
          const getNode = this.getWorkflowGetNodeOrNothing(draft);
          if (!getNode?.query) {
            return nothing;
          }

          getNode.query.logicalType = logicalType;
          return undefined;
        }),
      ),
      this.updateWorkflowAndTriggerUpdate(workflowId),
    );
  }

  // #endregion filter conditions

  setDataNodeAssignments(workflowId: string, assignments: DataAssignment[]): Observable<Workflow> {
    return this.selectWorkflowOnce(workflowId).pipe(
      map((workflow) =>
        produce<Workflow | undefined>(workflow, (draft) => {
          if (!draft) {
            return nothing;
          } // return undefined
          const dataNode =
            findFirstNodeInWorkflowOfType(draft, isInsertNode) ||
            findFirstNodeInWorkflowOfType(draft, isUpdateNode);
          if (!dataNode) {
            return nothing;
          }
          dataNode.assignments = assignments;
          return undefined;
        }),
      ),
      this.updateWorkflowAndTriggerUpdate(workflowId),
    );
  }

  ensureWorkflowInputsExist(elementId: CanvasElementId): Observable<Workflow> {
    return this.store.select(fromEngine.selectConnectionsAndEntities).pipe(
      withLatestFrom(this.store.select(runtimeSelectors.selectInputWorkflowForElement(elementId))),
      take(1),
      concatMap(([{ connections, entities }, workflow]) => {
        const { inputConnections, inputNames } = getNamedInputConnections(
          connections,
          workflow,
          workflow?.id ?? '',
        );

        const inputs = inputConnections
          .map((connection) => {
            const elementId: string = connection.source.id;
            const element: CanvasElement | undefined = entities[elementId];
            let inputName = inputNames[connection.id] || '';
            if (!inputName && element) {
              inputName = getWorkflowInputName(element);
            }
            return { [inputName]: connection.id };
          })
          .reduce((acc, inp) => ({ ...acc, ...inp }), {});

        if (workflow) {
          const inputsEntries = Object.entries(inputs);
          const newWorkflow = produce(workflow, (draft: Workflow) => {
            inputsEntries.forEach(([inputKey, inputValue]) => {
              draft.inputs[inputKey] = inputValue;
            });
          });
          return of(newWorkflow).pipe(this.updateWorkflowAndTriggerUpdate(workflow.id, elementId));
        }
        return EMPTY;
      }),
    );
  }

  selectWorkflowOnce(workflowId: string): Observable<Workflow | undefined> {
    return this.store.select(runtimeSelectors.selectWorkflowById(workflowId)).pipe(first());
  }

  createWorkflow(createWorkflowRequest: CreateWorkflowRequest): Observable<Workflow> {
    return this.workflowsApiService._create({
      ...createWorkflowRequest,
      pageId: isTriggeredWorkflow(createWorkflowRequest) ? null : createWorkflowRequest.pageId,
      version: DEFAULT_WORKFLOW_VERSION,
    });
  }

  deleteWorkflow(workflowId: string, updateConnectors = false): Observable<void> {
    if (!updateConnectors) {
      return this.workflowsApiService._delete(workflowId);
    }

    return this.connectorsCollService.entities$.pipe(
      first(),
      switchMap((connectors) => {
        if (!connectors?.length) {
          return of(true);
        }
        for (const connector of connectors) {
          const webhookWfIndex =
            connector.webhookWorkflows.findIndex((whf) => whf.workflowId === workflowId) ?? -1;
          if (webhookWfIndex === -1) {
            continue;
          }

          const updatedWebhookWfs = [...connector.webhookWorkflows];
          updatedWebhookWfs.splice(webhookWfIndex, 1);
          return this.connectorsCollService
            .update(
              { ...connector, webhookWorkflows: updatedWebhookWfs },
              { correlationId: connector.id },
            )
            .pipe(map(() => true));
        }
        return of(true);
      }),
      catchError((err) => {
        console.error(
          'Unable to remove webhook workflow from the connector. Workflow cannot be deleted.',
        );
        this.notificationService.showError(err);
        throw err;
      }),
      switchMap(() => {
        return this.workflowsApiService._delete(workflowId);
      }),
    );
  }

  /**
   * @param wf - Workflow to update
   * @param newConnections - A list of newly created connections to the workflow which might not be present in the state
   * @param newInputSchemas - A map of inputName to a schema of that input.
   * @returns
   */
  updateWorkflow(
    wf: Workflow,
    newConnections: (Connection | undefined)[] = [],
    newInputSchemas?: { [key: string]: BaseSchema | undefined },
  ): Observable<Workflow> {
    let wfWithUpdatedInputSchemas$ = of(wf);

    // Function workflows manage their own inputSchemas independently of UI connections,
    // so skip the connection-based rebuild that would overwrite them with an empty array.
    if (!isServerOnlyWorkflow(wf) && wf.workflowType !== 'Function' && wf.version === 2) {
      wfWithUpdatedInputSchemas$ = this.updateWorkflowInputSchemas(
        wf,
        newConnections,
        newInputSchemas,
      );
    }

    return wfWithUpdatedInputSchemas$.pipe(
      map((wfWithInputSchemas) => {
        const request: UpdateWorkflowRequest = {
          ...wfWithInputSchemas,
          inputSchemas: wfWithInputSchemas.inputSchemas || [],
          pageId: isTriggeredWorkflow(wfWithInputSchemas) ? null : wfWithInputSchemas.pageId,
          version: wfWithInputSchemas.version ?? DEFAULT_WORKFLOW_VERSION,
        };
        return { request, wfWithInputSchemas };
      }),
      switchMap(({ request, wfWithInputSchemas }) => {
        return this.workflowsApiService._update(wf.id, request).pipe(
          map((updatedWorkflow) => {
            if (updatedWorkflow.workflowType === 'Function') {
              return updatedWorkflow;
            }

            // Backend doesn't return the property "inputSchemas"for non-function workflows because it significantly slows down performance.
            // We keep track of it ourselves by adding it to the returned object when update is successful.

            return {
              ...updatedWorkflow,
              inputSchemas: deepClone(wfWithInputSchemas.inputSchemas) || [],
            };
          }),
        );
      }),
    );
  }

  /**
   * Updates input schemas of a workflow. Does not update Server workflow schemas.
   * @param workflow
   * @param newConnections
   * @param newInputSchemas
   * @returns
   */
  updateWorkflowInputSchemas(
    workflow: Workflow,
    newConnections: (Connection | undefined)[] = [],
    newInputSchemas?: { [key: string]: BaseSchema | undefined },
  ): Observable<Workflow> {
    if (isServerOnlyWorkflow(workflow)) {
      console.warn(`Cannot update input schemas for server only workflow`, workflow);
      return of(workflow);
    }

    const inputSchemas: InputSchemaInfo[] = [];
    const parsedInputSchemas = new Set<string>();

    // Filter out defined connections.
    const definedNewConnections: Connection[] = [];
    for (let i = 0; i < newConnections?.length; i++) {
      const conn = newConnections[i];
      if (conn) {
        definedNewConnections.push(conn);
      }
    }

    // User specified a mapping of input schemas. This assumes that workflow already has defined inputs. Used in order
    // to properly update workflows generated in the namespace for which we don't have connection information.
    if (newInputSchemas) {
      // Check whether inputs specified in the workflow and passed inputSchemaMapping match keys.
      const inputNames = Object.keys(newInputSchemas).toSorted(compareAsc);
      const inputSchemaNames = Object.keys(workflow?.inputs || {}).toSorted(compareAsc);
      if (!isEqual(inputNames, inputSchemaNames)) {
        console.error(`Mismatch between inputs and input schemas`, inputNames, inputSchemaNames);
      }
      for (const inputName of inputNames) {
        const schema = newInputSchemas[inputName];
        if (schema) {
          const inputSchema = convertSchemaToInputSchemaInfo(schema, inputName);
          inputSchemas.push(inputSchema);
          parsedInputSchemas.add(inputName);
        }
      }
    }

    // Parse connections to deduce inputs to the workflow and create inputSchemas.
    return combineLatest({
      connections: this.runtimeFacade.connections$,
      elements: this.elementsFacade.elements$,
      schemas: this.runtimeFacade.schemas$,
    }).pipe(
      first(),
      map(({ connections, elements, schemas }) => {
        // Compare workflows
        const parsedInputs = new Set<string>();
        const inputs: WorkflowInputs = {};

        const currInputs = { ...workflow.inputs };
        let connectionsToWf = connections.filter((connection) => {
          return connection.target.layerType === 'Workflow' && connection.target.id === workflow.id;
        });

        if (newConnections) {
          connectionsToWf = connectionsToWf.concat(definedNewConnections);
        }

        for (const connection of connectionsToWf) {
          const sourceId = connection.source.id;
          const element = elements.find(({ id }) => id === sourceId);
          const sourceElementSchema = getElementSchema(sourceId, schemas, connection.eventType);

          // Add existing inputs and schemas if they still exist
          for (const existingInputName in workflow.inputs) {
            const existingInputConnectionId = workflow.inputs[existingInputName];
            if (existingInputConnectionId === connection.id) {
              if (!parsedInputs.has(existingInputName)) {
                inputs[existingInputName] = existingInputConnectionId;
                parsedInputs.add(existingInputName);
              }
              // Choose either user-passed schema or use found element schema.
              const sourceSchema = newInputSchemas?.[existingInputName] || sourceElementSchema;
              if (sourceSchema && !parsedInputSchemas.has(existingInputName)) {
                const inputSchema = convertSchemaToInputSchemaInfo(sourceSchema, existingInputName);
                inputSchemas.push(inputSchema);
                parsedInputSchemas.add(existingInputName);
              }
            }
          }

          if (element) {
            const inputName = getWorkflowInputName(element, connection, workflow);
            // Choose either user-passed schema or use found element schema.
            const sourceSchema = newInputSchemas?.[inputName] || sourceElementSchema;
            if (sourceSchema) {
              const inputSchema = convertSchemaToInputSchemaInfo(sourceSchema, inputName);
              if (!parsedInputSchemas.has(inputName)) {
                inputSchemas.push(inputSchema);
                parsedInputSchemas.add(inputName);
              }
              if (!parsedInputs.has(inputName)) {
                inputs[inputName] = connection.id;
                parsedInputs.add(inputName);
              }
            }
          }
        }

        if (isDeepEqual(currInputs, inputs) || !isDeepEqual(workflow?.inputSchemas, inputSchemas)) {
          // Update input schemas of workflows
          return { ...workflow, inputSchemas, inputs };
        }

        return { ...workflow };
      }),
    );
  }

  /**
   * Saves the workflow workflowId
   * @param workflowId
   * @param triggerDatasetUpdate
   * @returns
   */
  private updateWorkflowAndTriggerUpdate(
    workflowId: string,
    elementId?: CanvasElementId,
  ): UnaryFunction<Observable<unknown>, Observable<Workflow>> {
    return pipe(
      filter((updatedWorkflow): updatedWorkflow is Workflow => {
        if (!updatedWorkflow) {
          console.error(`Workflow ${workflowId} or proper node could not be found`);
        }
        return !!updatedWorkflow;
      }),
      concatMap((updatedWorkflow) =>
        this.updateWorkflow({ ...updatedWorkflow, id: workflowId }).pipe(
          catchError((error) => {
            this.notificationService.error('Error when saving changes');
            console.error('Could not save workflow', error, updatedWorkflow);
            return EMPTY;
          }),
        ),
      ),
      tap((savedWorkflow) => {
        this.store.dispatch(runtimeActions.workflowUpdate({ workflow: savedWorkflow, elementId }));
      }),
    );
  }

  private getWorkflowGetNodeOrNothing(draft: WritableDraft<Workflow>): GetWorkflowNode | undefined {
    const getNode = findFirstNodeInWorkflowOfType(draft, isGetNode);
    if (!getNode) {
      return undefined;
    }

    if (getNode.queryConditions?.length) {
      getNode.query = mapSimpleQueryConditionsToComplexFilters(
        getNode.queryConditions,
        getNode.query?.logicalType,
      );
      getNode.queryConditions = [];
    }
    return getNode;
  }
}
